import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../context/AppContext";
import { academicYearService, financialYearService } from "../services/yearService";
import dashboardService from "../services/dashboardService";
import {
    FiUsers, FiBriefcase, FiRefreshCw, FiUserPlus,
    FiCalendar, FiSettings, FiGift, FiList, FiGrid,
    FiCheckSquare, FiMessageSquare
} from "react-icons/fi";
import UpcomingEventsWidget from "../components/UpcomingEventsWidget";
import ScholarshipSummaryWidget from "../components/ScholarshipSummaryWidget";
import HostelSummaryWidget from "../components/HostelSummaryWidget";
import EscalationWidget from "../components/EscalationWidget";
import CollegeLogoBadge from "../components/common/CollegeLogoBadge";

// ─── Styles ──────────────────────────────────────────────────────────────────

const cardStyle = {
    padding: "1.1rem 1.2rem",
    display: "grid",
    gap: "0.6rem",
};

const sectionTitleStyle = {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--text-muted)",
    fontWeight: 700,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const cleanWhatsappNumber = (value = "") => {
    const digits = String(value).replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length === 10) return `91${digits}`;
    if (digits.length === 12 && digits.startsWith("91")) return digits;
    return digits;
};

const buildBirthdayMessage = (entry) => {
    const name = entry.fullName?.split(" ")[0] || entry.fullName || "there";
    if (entry.type === "student") {
        return `Happy Birthday ${name}! Wishing you a wonderful day filled with happiness, success, and good health. Best wishes from Central India College of Pharmacy. 🎂💐🎉`;
    }
    return `Happy Birthday ${name}! Wishing you a joyful day and a wonderful year ahead. Best wishes from Central India College of Pharmacy. 🎂💐🎉`;
};

// ─── Shimmer skeleton ─────────────────────────────────────────────────────────

const shimmerKeyframes = `
@keyframes shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
}
@keyframes spin-once {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}
`;

function SkeletonBox({ width = "100%", height = 14, radius = 6, style = {} }) {
    return (
        <div
            style={{
                width,
                height,
                borderRadius: radius,
                background:
                    "linear-gradient(90deg, var(--border, #e2e8f0) 25%, var(--bg-hover, #f8fafc) 50%, var(--border, #e2e8f0) 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.4s ease-in-out infinite",
                ...style,
            }}
        />
    );
}

function CardSkeleton() {
    return (
        <div className="card" style={cardStyle}>
            <SkeletonBox width="50%" height={11} />
            <SkeletonBox width="90%" height={14} />
            <SkeletonBox width="75%" height={14} />
            <SkeletonBox width="80%" height={14} />
        </div>
    );
}

// ─── Donut Ring ───────────────────────────────────────────────────────────────

function DonutRing({ value = 0, total = 0, color = "#003b67", size = 76, strokeWidth = 7 }) {
    const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
    const r = (size - strokeWidth * 2) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;

    return (
        <svg width={size} height={size} aria-label={`${Math.round(pct)}%`}>
            <circle
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke={`${color}22`}
                strokeWidth={strokeWidth}
            />
            <circle
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray={circ}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform={`rotate(-90 ${cx} ${cy})`}
                style={{ transition: "stroke-dashoffset 0.8s ease" }}
            />
            <text
                x={cx} y={cy - 5}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 14, fontWeight: 700, fill: color }}
            >
                {Math.round(pct)}%
            </text>
            <text
                x={cx} y={cy + 10}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 9, fill: "var(--text-muted, #94a3b8)", fontWeight: 600 }}
            >
                present
            </text>
        </svg>
    );
}

// ─── Summary Row ──────────────────────────────────────────────────────────────

function SummaryRow({ label, active, droppedOut, total, value }) {
    const showStatusSplit =
        typeof active === "number" || typeof droppedOut === "number";

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: showStatusSplit
                    ? "minmax(110px, 1fr) repeat(3, auto)"
                    : "minmax(110px, 1fr) auto",
                gap: "0.5rem",
                alignItems: "center",
                fontSize: 13,
                padding: "0.25rem 0",
                borderBottom: "1px solid var(--border, #f1f5f9)",
            }}
        >
            <div style={{ fontWeight: 600, color: "var(--text)" }}>{label}</div>
            {showStatusSplit ? (
                <>
                    {typeof total === "number" && (
                        <div style={{ color: "var(--text-muted)" }}>
                            Total: <strong style={{ color: "var(--text)" }}>{total}</strong>
                        </div>
                    )}
                    <div style={{ color: "#166534" }}>
                        Active: <strong>{active ?? 0}</strong>
                    </div>
                    <div style={{ color: "#b91c1c" }}>
                        Dropped: <strong>{droppedOut ?? 0}</strong>
                    </div>
                </>
            ) : (
                <div style={{ color: "var(--text-muted)" }}>
                    {typeof total === "number" ? "Total" : "Count"}:{" "}
                    <strong style={{ color: "var(--text)" }}>{total ?? value ?? 0}</strong>
                </div>
            )}
        </div>
    );
}

// ─── Summary Card (Students / Employees) ─────────────────────────────────────

function SummaryCard({ title, icon: Icon, accentColor, children, loading }) {
    return (
        <div
            className="card"
            style={{
                ...cardStyle,
                borderLeft: `4px solid ${accentColor}`,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* faint background icon */}
            <div
                style={{
                    position: "absolute",
                    top: 8,
                    right: 12,
                    opacity: 0.06,
                    pointerEvents: "none",
                }}
            >
                <Icon size={56} color={accentColor} />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        background: `${accentColor}18`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon size={15} color={accentColor} />
                </span>
                <div style={sectionTitleStyle}>{title}</div>
            </div>

            {loading ? (
                <div style={{ display: "grid", gap: "0.5rem", paddingTop: "0.25rem" }}>
                    <SkeletonBox width="90%" />
                    <SkeletonBox width="70%" />
                    <SkeletonBox width="80%" />
                </div>
            ) : (
                children
            )}
        </div>
    );
}

// ─── Attendance Block ─────────────────────────────────────────────────────────

function AttendanceBlock({ title, data, color = "#003b67" }) {
    const statuses = [
        { label: "Present", value: data?.present || 0, color: "#166534" },
        { label: "Absent", value: data?.absent || 0, color: "#b91c1c" },
        { label: "Leave", value: data?.leave || 0, color: "#003b67" },
        { label: "Late", value: data?.late || 0, color: "#b45309" },
        { label: "Unmarked", value: data?.unmarked || 0, color: "#6b7280" },
    ];

    const markedPct =
        data?.total > 0
            ? Math.round(((data?.marked || 0) / data.total) * 100)
            : 0;

    return (
        <div className="card" style={cardStyle}>
            <div style={sectionTitleStyle}>{title}</div>

            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                {/* Donut */}
                <DonutRing
                    value={data?.present || 0}
                    total={data?.total || 0}
                    color={color}
                    size={76}
                    strokeWidth={7}
                />

                {/* Stats */}
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>
                        {data?.total ?? 0}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                        total &nbsp;·&nbsp; {data?.marked ?? 0} marked ({markedPct}%)
                    </div>

                    {/* Mini progress bar for marking coverage */}
                    <div
                        style={{
                            height: 4,
                            borderRadius: 4,
                            background: `${color}18`,
                            overflow: "hidden",
                            marginBottom: "0.6rem",
                        }}
                    >
                        <div
                            style={{
                                height: "100%",
                                width: `${markedPct}%`,
                                background: color,
                                borderRadius: 4,
                                transition: "width 0.8s ease",
                            }}
                        />
                    </div>

                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                        {statuses.map((s) => (
                            <span
                                key={s.label}
                                className="badge"
                                style={{
                                    background: `${s.color}12`,
                                    color: s.color,
                                    padding: "3px 7px",
                                    fontSize: 11.5,
                                }}
                            >
                                {s.label}: <strong>{s.value}</strong>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Birthday Card ────────────────────────────────────────────────────────────

function BirthdayCard({ entry, onSendWishes }) {
    return (
        <div
            className="card"
            style={{
                padding: "0.9rem",
                display: "grid",
                gap: "0.8rem",
                borderLeft: entry.isToday ? "3px solid #dc2626" : undefined,
            }}
        >
            <div style={{ display: "flex", gap: "0.85rem", alignItems: "center" }}>
                <div
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: 14,
                        overflow: "hidden",
                        background: entry.isToday ? "#fee2e2" : "#e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: entry.isToday ? "#dc2626" : "#475569",
                        fontWeight: 700,
                        fontSize: 15,
                        flexShrink: 0,
                    }}
                >
                    {entry.photoUrl ? (
                        <img
                            src={entry.photoUrl}
                            alt={entry.fullName}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    ) : (
                        entry.fullName
                            .split(" ")
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((part) => part[0]?.toUpperCase())
                            .join("")
                    )}
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{entry.fullName}</div>
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{entry.metaLine}</div>
                    <span
                        style={{
                            display: "inline-block",
                            marginTop: 3,
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "2px 7px",
                            borderRadius: 20,
                            background: entry.isToday ? "#fee2e2" : "#f1f5f9",
                            color: entry.isToday ? "#dc2626" : "var(--text-muted)",
                        }}
                    >
                        {entry.isToday
                            ? "🎂 Birthday Today!"
                            : `In ${entry.daysUntil} day${entry.daysUntil !== 1 ? "s" : ""}`}
                    </span>
                </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    DOB: {entry.dobValue || "–"}
                </div>
                <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
                    onClick={() => onSendWishes(entry)}
                >
                    <FiMessageSquare size={12} />
                    Send Wishes
                </button>
            </div>
        </div>
    );
}

// ─── Birthday List Item (compact mode) ───────────────────────────────────────

function BirthdayListItem({ entry, onSendWishes }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.5rem 0",
                borderBottom: "1px solid var(--border, #f1f5f9)",
            }}
        >
            {/* Avatar */}
            <div
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    overflow: "hidden",
                    background: entry.isToday ? "#fee2e2" : "#e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: entry.isToday ? "#dc2626" : "#475569",
                    fontWeight: 700,
                    fontSize: 12,
                    flexShrink: 0,
                }}
            >
                {entry.photoUrl ? (
                    <img
                        src={entry.photoUrl}
                        alt={entry.fullName}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                ) : (
                    entry.fullName
                        .split(" ")
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((p) => p[0]?.toUpperCase())
                        .join("")
                )}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{entry.fullName}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{entry.metaLine}</div>
            </div>

            {/* Badge + Action */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                <span
                    style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "2px 7px",
                        borderRadius: 20,
                        background: entry.isToday ? "#fee2e2" : "#f1f5f9",
                        color: entry.isToday ? "#dc2626" : "var(--text-muted)",
                        whiteSpace: "nowrap",
                    }}
                >
                    {entry.isToday ? "Today 🎂" : `${entry.daysUntil}d`}
                </span>
                <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ padding: "4px 8px" }}
                    onClick={() => onSendWishes(entry)}
                    title="Send WhatsApp Wishes"
                >
                    <FiMessageSquare size={12} />
                </button>
            </div>
        </div>
    );
}

// ─── Birthday Section ─────────────────────────────────────────────────────────

function BirthdaySection({ title, entries, onSendWishes, viewMode, onToggleView, icon: Icon }) {
    const isEmpty = !entries?.length;

    return (
        <div className="card" style={{ marginBottom: "1rem" }}>
            <div
                className="card-header"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <Icon size={14} />
                    {title}
                    {!isEmpty && (
                        <span
                            style={{
                                marginLeft: 4,
                                fontSize: 11,
                                fontWeight: 700,
                                padding: "1px 6px",
                                borderRadius: 20,
                                background: "var(--primary, #003b67)",
                                color: "#fff",
                            }}
                        >
                            {entries.length}
                        </span>
                    )}
                </div>

                {!isEmpty && (
                    <div style={{ display: "flex", gap: "0.25rem" }}>
                        <button
                            type="button"
                            className="btn btn-sm"
                            title="Card view"
                            onClick={() => onToggleView("card")}
                            style={{
                                padding: "4px 7px",
                                background: viewMode === "card" ? "var(--primary, #003b67)" : "transparent",
                                color: viewMode === "card" ? "#fff" : "var(--text-muted)",
                                border: "1px solid var(--border, #e2e8f0)",
                                borderRadius: 6,
                                cursor: "pointer",
                            }}
                        >
                            <FiGrid size={12} />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm"
                            title="List view"
                            onClick={() => onToggleView("list")}
                            style={{
                                padding: "4px 7px",
                                background: viewMode === "list" ? "var(--primary, #003b67)" : "transparent",
                                color: viewMode === "list" ? "#fff" : "var(--text-muted)",
                                border: "1px solid var(--border, #e2e8f0)",
                                borderRadius: 6,
                                cursor: "pointer",
                            }}
                        >
                            <FiList size={12} />
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body">
                {isEmpty ? (
                    <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
                        No birthdays to show.
                    </div>
                ) : viewMode === "card" ? (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                            gap: "0.9rem",
                        }}
                    >
                        {entries.map((entry) => (
                            <BirthdayCard
                                key={`${entry.type}-${entry._id}`}
                                entry={entry}
                                onSendWishes={onSendWishes}
                            />
                        ))}
                    </div>
                ) : (
                    <div>
                        {entries.map((entry) => (
                            <BirthdayListItem
                                key={`${entry.type}-${entry._id}`}
                                entry={entry}
                                onSendWishes={onSendWishes}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Quick Action Button ──────────────────────────────────────────────────────

function QuickActionBtn({ to, icon: Icon, label, variant = "outline", color }) {
    return (
        <Link
            to={to}
            className={`btn btn-${variant}`}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                ...(color ? { borderColor: color, color } : {}),
            }}
        >
            <Icon size={14} />
            {label}
        </Link>
    );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export default function Dashboard() {
    const navigate = useNavigate();
    const {
        academicYear,
        financialYear,
        selectAcademicYear,
        selectFinancialYear,
        collegeName,
    } = useApp();

    const [allAY, setAllAY] = useState([]);
    const [allFY, setAllFY] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Birthday view modes: "card" | "list"
    const [todayView, setTodayView] = useState("card");
    const [upcomingView, setUpcomingView] = useState("card");

    useEffect(() => {
        const loadYears = async () => {
            try {
                const [ayRes, fyRes] = await Promise.all([
                    academicYearService.getAll(),
                    financialYearService.getAll(),
                ]);
                setAllAY(ayRes.data);
                setAllFY(fyRes.data);

                if (!academicYear) {
                    const active = ayRes.data.find((y) => y.isActive);
                    if (active) selectAcademicYear(active);
                }
                if (!financialYear) {
                    const active = fyRes.data.find((y) => y.isActive);
                    if (active) selectFinancialYear(active);
                }
            } catch (err) {
                toast.error("Could not load year list: " + err.message);
            }
        };

        loadYears();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSummary = useCallback(async () => {
        if (!academicYear?._id) {
            setSummary(null);
            return;
        }

        setLoadingSummary(true);
        try {
            const res = await dashboardService.getSummary({ academicYear: academicYear._id });
            setSummary(res.data);
        } catch (err) {
            toast.error("Could not load dashboard summary: " + err.message);
        } finally {
            setLoadingSummary(false);
        }
    }, [academicYear]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadSummary();
        setTimeout(() => setRefreshing(false), 600);
    };

    const handleAYChange = (e) => {
        const selected = allAY.find((y) => y._id === e.target.value);
        if (selected) selectAcademicYear(selected);
    };

    const handleFYChange = (e) => {
        const selected = allFY.find((y) => y._id === e.target.value);
        if (selected) selectFinancialYear(selected);
    };

    const handleSendWishes = (entry) => {
        const preferredNumber = entry.mobile || entry.alternateMobile;
        const cleanedNumber = cleanWhatsappNumber(preferredNumber);

        if (!cleanedNumber) {
            toast.error("No WhatsApp number is available for this record.");
            return;
        }

        const encodedMessage = encodeURIComponent(buildBirthdayMessage(entry));
        const whatsappUrl = `https://wa.me/${cleanedNumber}?text=${encodedMessage}`;
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
        toast.success(`Opening WhatsApp wishes for ${entry.fullName}.`);
    };

    return (
        <div>
            {/* Inject keyframe animations */}
            <style>{shimmerKeyframes}</style>

            {/* ── Page Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {collegeName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>Dashboard</h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>
                            College Management System
                        </p>
                    </div>
                </div>

                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end" }}>
                    {/* Academic Year */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <label style={sectionTitleStyle}>Academic Year</label>
                        {allAY.length === 0 ? (
                            <button className="btn btn-outline btn-sm" onClick={() => navigate("/settings")}>
                                + Create Year
                            </button>
                        ) : (
                            <select
                                className="form-control"
                                style={{ width: 160, fontSize: 13, fontWeight: 600, color: "#003b67" }}
                                value={academicYear?._id || ""}
                                onChange={handleAYChange}
                            >
                                <option value="">— Select —</option>
                                {allAY.map((y) => (
                                    <option key={y._id} value={y._id}>
                                        {y.label}{y.isActive ? " *" : ""}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Financial Year */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <label style={sectionTitleStyle}>Financial Year</label>
                        {allFY.length === 0 ? (
                            <button className="btn btn-outline btn-sm" onClick={() => navigate("/settings")}>
                                + Create Year
                            </button>
                        ) : (
                            <select
                                className="form-control"
                                style={{ width: 185, fontSize: 13, fontWeight: 600, color: "#15803d" }}
                                value={financialYear?._id || ""}
                                onChange={handleFYChange}
                            >
                                <option value="">— Select —</option>
                                {allFY.map((y) => (
                                    <option key={y._id} value={y._id}>
                                        {y.label}{y.isActive ? " *" : ""}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Refresh */}
                    {academicYear && (
                        <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={handleRefresh}
                            disabled={loadingSummary}
                            title="Refresh dashboard"
                            style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                        >
                            <FiRefreshCw
                                size={13}
                                style={{
                                    animation: refreshing
                                        ? "spin-once 0.6s linear"
                                        : "none",
                                }}
                            />
                            Refresh
                        </button>
                    )}
                </div>
            </div>

            {/* ── No Academic Year Warning ── */}
            {!academicYear && (
                <div
                    style={{
                        background: "#fef3c7",
                        border: "1px solid #fcd34d",
                        borderRadius: 8,
                        padding: "0.85rem 1rem",
                        marginBottom: "1.25rem",
                        fontSize: 13,
                        color: "#92400e",
                    }}
                >
                    <strong>No academic year selected.</strong> Please select or{" "}
                    <Link to="/settings" style={{ color: "#92400e", fontWeight: 600 }}>
                        create an academic year
                    </Link>{" "}
                    to view dashboard data.
                </div>
            )}

            {/* ── Summary Cards ── */}
            {academicYear && (
                <>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                            gap: "1rem",
                            marginBottom: "1rem",
                        }}
                    >
                        {/* Students */}
                        <SummaryCard
                            title="Students"
                            icon={FiUsers}
                            accentColor="#003b67"
                            loading={loadingSummary}
                        >
                            <SummaryRow
                                label="Total Students"
                                total={summary?.studentCard?.totalStudents?.total || 0}
                                active={summary?.studentCard?.totalStudents?.active || 0}
                                droppedOut={summary?.studentCard?.totalStudents?.droppedOut || 0}
                            />
                            <SummaryRow
                                label="Boys"
                                active={summary?.studentCard?.boys?.active || 0}
                                droppedOut={summary?.studentCard?.boys?.droppedOut || 0}
                            />
                            <SummaryRow
                                label="Girls"
                                active={summary?.studentCard?.girls?.active || 0}
                                droppedOut={summary?.studentCard?.girls?.droppedOut || 0}
                            />
                        </SummaryCard>

                        {/* Employees */}
                        <SummaryCard
                            title="Employees"
                            icon={FiBriefcase}
                            accentColor="#059669"
                            loading={loadingSummary}
                        >
                            <SummaryRow
                                label="Total Employees"
                                value={summary?.employeeCard?.totalEmployees || 0}
                            />
                            <SummaryRow
                                label="Teaching"
                                value={summary?.employeeCard?.teaching || 0}
                            />
                            <SummaryRow
                                label="Non Teaching"
                                value={summary?.employeeCard?.nonTeaching || 0}
                            />
                        </SummaryCard>
                    </div>

                    {/* ── Attendance ── */}
                    {loadingSummary ? (
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: "1rem",
                                marginBottom: "1rem",
                            }}
                        >
                            <CardSkeleton />
                            <CardSkeleton />
                        </div>
                    ) : (
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: "1rem",
                                marginBottom: "1rem",
                            }}
                        >
                            <AttendanceBlock
                                title="Today's Student Attendance"
                                data={summary?.todayAttendance?.students}
                                color="#003b67"
                            />
                            <AttendanceBlock
                                title="Today's Employee Attendance"
                                data={summary?.todayAttendance?.employees}
                                color="#059669"
                            />
                        </div>
                    )}



                    {/* ── Scholarships ── */}
                    <div style={{ marginBottom: "1rem" }}>
                        <ScholarshipSummaryWidget />
                    </div>

                    {/* ── Hostel Occupancy ── */}
                    <HostelSummaryWidget />
                </>
            )}

            {/* ── Super Admin Escalations & Resource Requests Widget ── */}
            <EscalationWidget />

            <UpcomingEventsWidget />
            {/* ── Birthdays ── */}
            {loadingSummary ? (
                <>
                    <div className="card" style={{ marginBottom: "1rem" }}>
                        <div className="card-header">Today's Birthdays</div>
                        <div className="card-body">
                            <SkeletonBox width="60%" height={13} />
                        </div>
                    </div>
                    <div className="card" style={{ marginBottom: "1rem" }}>
                        <div className="card-header">Upcoming Birthdays (Next 7 Days)</div>
                        <div className="card-body">
                            <SkeletonBox width="60%" height={13} />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <BirthdaySection
                        title="Today's Birthdays"
                        entries={summary?.birthdays?.today || []}
                        onSendWishes={handleSendWishes}
                        viewMode={todayView}
                        onToggleView={setTodayView}
                        icon={FiGift}
                    />
                    <BirthdaySection
                        title="Upcoming Birthdays (Next 7 Days)"
                        entries={summary?.birthdays?.upcoming || []}
                        onSendWishes={handleSendWishes}
                        viewMode={upcomingView}
                        onToggleView={setUpcomingView}
                        icon={FiCalendar}
                    />
                </>
            )}

            {/* ── Quick Actions ── */}
            <div className="card">
                <div className="card-header">Quick Actions</div>
                <div
                    className="card-body"
                    style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}
                >
                    <QuickActionBtn to="/students/add" icon={FiUserPlus} label="Add Student" variant="primary" />
                    <QuickActionBtn to="/employees/add" icon={FiUserPlus} label="Add Employee" variant="primary" />
                    <QuickActionBtn to="/attendance/mark" icon={FiCheckSquare} label="Mark Student Attendance" variant="outline" />
                    <QuickActionBtn to="/employees/attendance/add" icon={FiCheckSquare} label="Mark Employee Attendance" variant="outline" />
                    <QuickActionBtn to="/settings" icon={FiSettings} label="Manage Years" variant="outline" />
                </div>
            </div>
        </div>
    );
}